import {FC, useState} from "react";
import {useParams} from "react-router";
import styles from './EventPage.module.css';
import {ApproversCard} from "./components/ApproversCard/ApproversCard.tsx";
import {useEvent} from "../../queries/useEvent.ts";
import {App, Empty, Flex, Spin, theme, Tooltip, Typography} from "antd";
import {CloseCircleFilled, DeleteOutlined, FormatPainterFilled} from "@ant-design/icons";
import {EventActions} from "../../components/EventActions/EventActions.tsx";
import {eventsEventIdPatch} from "../../api/events/eventsEventIdPatch.ts";
import {useOwnProfile} from "../../stores/OwnProfileStore.ts";
import {EventInfoCard} from "./components/EventInfoCard/EventInfoCard.tsx";
import {useEventApprovers} from "../../queries/useEventApprovers.ts";
import {UserWithContacts} from "../../components/UserWithContacts/UserWithContacts.tsx";
import {useIsMobile} from "../../hooks/useIsMobile.ts";
import {useEventComments} from "../../queries/useEventComments.ts";

export const EventPage: FC = () => {
    const isMobile = useIsMobile();

    const { profile } = useOwnProfile();
    const { message } = App.useApp();
    const { token } = theme.useToken();
    const { eventId } = useParams();
    const { data: event, refetch: refetchEvent } = useEvent(eventId!);
    const { data: approvers, refetch: refetchApprovers } = useEventApprovers(eventId!);
    const { data: comments, refetch: refetchComments } = useEventComments(eventId!);

    const refetch = () => {
        void refetchEvent();
        void refetchApprovers();
        void refetchComments();
    }

    const editable = !approvers?.every((r) => r.status === 'approved') &&
        (event?.approvable ||
        profile?.id === event?.created_by.id ||
        profile?.role === 'staff');

    const [loading, setLoading] = useState(false);
    const handleTitleEdit = (field: 'name' | 'name_en') => (value: string) => {
        setLoading(true);
        eventsEventIdPatch(event!.id, {
            [field]: value
        }).then(() => {
            void message.success('Сохранено');
            void refetch();
        }).catch((e) => {
            console.error(e);
            void message.error(`Ошибка (${JSON.stringify(e)})`);
        }).finally(() => setLoading(false));
    }

    return (
        <Flex vertical gap={8} flex="1 0">
            <Flex align="center" justify="space-between" vertical={isMobile} gap={isMobile ? 16 : undefined}>
                <Flex align="center" gap={8}>
                    {event?.draft && (
                        <Tooltip title="Черновик">
                            <FormatPainterFilled style={{ color: token.colorWarning, fontSize: '24px' }}/>
                        </Tooltip>
                    )}
                    {event?.returned && (
                        <Tooltip title="Не согласовано">
                            <CloseCircleFilled style={{ color: token.colorError, fontSize: '24px' }}/>
                        </Tooltip>
                    )}
                    {event?.cancelled && (
                        <Tooltip title="Отменено">
                            <DeleteOutlined style={{ color: token.colorError, fontSize: '24px' }}/>
                        </Tooltip>
                    )}
                    <Flex align="center" gap={8}>
                        <Typography.Title
                            level={2}
                            editable={editable ? { onChange: handleTitleEdit('name') } : undefined}
                        >{event?.name}</Typography.Title>
                        <Typography.Title level={2} type="secondary">/</Typography.Title>
                        <Typography.Title
                            level={2}
                            type="secondary"
                            editable={editable ? { onChange: handleTitleEdit('name_en') } : undefined}
                        >{event?.name_en || '—'}</Typography.Title>
                    </Flex>
                </Flex>
                {event && event.accessible && <EventActions event={event} onUpdate={refetch} fullPage />}
            </Flex>
            {event && <Flex gap={4}>
                <Typography.Text type="secondary" className={styles.initiator}>Инициатор согласования:</Typography.Text>
                <Typography.Text className={styles.name}>
                    <UserWithContacts user={event.created_by}/>
                </Typography.Text>
            </Flex>}
            <div className={styles.container}>
                {event?.accessible && eventId && <ApproversCard eventId={eventId} approvers={approvers} approvable={event?.approvable || false} onUpdate={refetch} editable={editable} />}
                {event && event.accessible && <EventInfoCard event={event} eventComments={comments} onUpdate={refetch} editable={editable} approvers={approvers} />}
                {event && !event.accessible && (
                    <Flex className={styles.emptyContainer} align="center" justify="center">
                        <Empty description="Недостаточно прав для просмотра этого мероприятия" />
                    </Flex>
                )}
            </div>
            <Spin spinning={loading} fullscreen />
        </Flex>
    )
}